import React, { useEffect, useState } from 'react';
import { Button, ProgressBar, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import getCurrentUser from '../../auth/getCurrentUser';
import { useAppDispatch } from '../reducer/reduxHooks';
import { setIsRetake } from '../reducer/actions';
import { dataService } from '../../services/DataService';
import IsLoading from '../IsLoading';
import CustomModal from '../CustomModal';
import RiskBar from '../results/RiskBar';
import ResultDetails from '../results/ResultDetails';
import RetakeOverviewTable from '../retakes/RetakesOverviewTable';
import getLocalDateStringFromTimestamp from '../../utils/getLocalDateStringFromTimestamp';

function getLatestAttempt(attempts: Array<Record<string, any>>) {
  if (!Array.isArray(attempts) || attempts.length === 0) {
    return null;
  }

  return attempts.reduce((latestAttempt, currentAttempt) => (
    currentAttempt.timestamp > latestAttempt.timestamp ? currentAttempt : latestAttempt
  ));
}

function AssessmentOverviewTable() {
  const [modules, setModules] = useState<Array<Record<string, any>> | any>();
  const [attemptsHistory, setAttemptsHistory] = useState<Array<Record<string, any>>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');

  useEffect(() => {
    const user = getCurrentUser();
    async function getData() {
      const modulesRes = await dataService.getItems('modules');
      const attemptsRes = await dataService.processAttempts(user?.uid);

      if (modulesRes.statusCode === 200) {
        setModules(modulesRes.body);
        setAttemptsHistory(attemptsRes.statusCode === 200 ? attemptsRes.body : []);
      }
      setIsLoading(false);
    }
    getData();
  }, []);

  if (isLoading) {
    return <IsLoading />;
  }
  if (attemptsHistory.length > 0) {
    return (
      <Table responsive striped className="assessment-overview-table">
        <caption>{t('pages.assessmentOverview.table.caption')}</caption>
        <colgroup>
          <col className="module-col" />
          <col className="riskbar-col" />
          <col className="button-col" />
          <col className="button-col" />
          <col className="button-col" />
        </colgroup>
        <thead>
          <tr>
            <th>{t('pages.assessmentOverview.table.column.item1')}</th>
            <th>{t('pages.assessmentOverview.table.column.item2')}</th>
            <th> </th>
            <th> </th>
            <th> </th>
          </tr>
        </thead>
        <tbody>
          {modules
            .filter((module: Record<string, any>) => module.domain !== 'test' && module.status !== 'draft')
            .sort((a: { order: number; }, b: { order: number; }) => a.order - b.order)
            .reverse()
            .sort(
              (a: Record<string, string>, b: Record<string, string>) => {
                const domainA = a.domain.toUpperCase();
                const domainB = b.domain.toUpperCase();
                if (domainA < domainB) {
                  return -1;
                }
                if (domainA > domainB) {
                  return 1;
                }
                return 0;
              },
            )
            .reverse()
            .map((module: Record<string, string>) => {
              const moduleAttempts = attemptsHistory.find(
                (attemptHistory: Record<string, any>) => attemptHistory.id === module.id,
              )?.attempts ?? [];
              // set moduleName to selected language
              const moduleName = (i18next.language !== 'de') ? module[`title_${i18next.language}`] ?? module.title : module.title;
              const displayedAssessment = getLatestAttempt(moduleAttempts);
              const hasAttempts = moduleAttempts.length >= 1;
              return (
                <tr key={moduleName}>
                  <td>
                    <Link to={`/modules/${module?.id}`}>{moduleName}</Link>
                  </td>
                  <td>
                    {(hasAttempts)
                      ? <RiskBar summaryRiskRating={displayedAssessment?.summaryRiskRating} />
                      : <ProgressBar now={0} />}
                  </td>
                  <td className="button-col">
                    {(hasAttempts)
                      ? (
                        <CustomModal
                          modalTitel={`${t('pages.assessmentOverview.modal.title')} ${moduleName}: Assessment, ${getLocalDateStringFromTimestamp(displayedAssessment?.timestamp)}`}
                          buttonText={t('pages.assessmentOverview.table.button.details')}
                          showPrintButton
                        >
                          <ResultDetails
                            module={module}
                            assessment={displayedAssessment ?? {}}
                            showSummary
                          />
                        </CustomModal>
                      )
                      : <Button variant="primary" disabled title={t('pages.assessmentOverview.table.button.disabled') ?? ''}>{t('pages.assessmentOverview.table.button.details')}</Button>}
                  </td>
                  <td className="button-col">
                    {(hasAttempts)
                      ? (
                        <Button
                          variant="primary"
                          onClick={() => {
                            dispatch(setIsRetake({ type: '', payload: { isRetake: true, moduleId: module.id } }));
                            navigate(`/modules/${module.id}/selbstcheck`);
                          }}
                        >
                          {t('pages.assessmentOverview.table.button.repeatSelfCheck')}
                        </Button>
                      )
                      : <Button variant="primary" disabled title={t('pages.assessmentOverview.table.button.disabled') ?? ''}>{t('pages.assessmentOverview.table.button.repeatSelfCheck')}</Button>}
                  </td>
                  <td className="button-col">
                    {(hasAttempts)
                      ? (
                        <CustomModal
                          fullscreen
                          modalTitel={`${t('pages.assessmentOverview.modal.title')} ${moduleName}`}
                          buttonText={t('pages.assessmentOverview.table.button.retake')}
                        >
                          <RetakeOverviewTable module={module} attempts={moduleAttempts} />
                        </CustomModal>
                      )
                      : <Button variant="primary" disabled title={t('pages.assessmentOverview.table.button.disabled') ?? ''}>{t('pages.assessmentOverview.table.button.retake')}</Button>}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    );
  }
  return (
    <div>
      {t('pages.assessmentOverview.text.noResults')}
      &nbsp;
      <Link to="/modules">{t('pages.assessmentOverview.text.goToModules')}</Link>
    </div>
  );
}

export default AssessmentOverviewTable;
